import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Upload, ChevronLeft, FileImage, Layers, Film, Image as ImageIcon } from 'lucide-react';

import { graphicAPI, maskAssetAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin, showAdminAccessDialog, ADMIN_ACTIONS } from '../../utils/adminUtils';
import MaskAssetUploadDialog from './MaskAssetUploadDialog';

const GraphicsSidebar = ({
  isOpen,
  onClose,
  onGraphicSelect,
  onOpenDialog,
  onMaskSelect = () => {},
  onImportSvgToCanvas,
  isDialog = false,
  dialogSearchQuery = '',
  viewMode = 'grid',
  defaultTab = 'graphics'
}) => {
  const { user } = useAuth();
  const [graphics, setGraphics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', color: '#6366F1' });
  const [uploading, setUploading] = useState(false);
  // Locally-uploaded SVGs (client-side library, persisted to localStorage).
  const [uploadedGraphics, setUploadedGraphics] = useState(() => {
    try {
      const raw = localStorage.getItem('editor.uploadedGraphics');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  });
  const [activeTab, setActiveTab] = useState(defaultTab || 'graphics');
  const [maskAssets, setMaskAssets] = useState([]);
  const [maskLoading, setMaskLoading] = useState(false);
  const [maskSearch, setMaskSearch] = useState('');
  const [maskTypeFilter, setMaskTypeFilter] = useState('all');
  const [showMaskDialog, setShowMaskDialog] = useState(false);

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token') || 'dev-token-' + Date.now();
  };

  // Fetch graphics
  const fetchGraphics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (effectiveSearchQuery) params.append('search', effectiveSearchQuery);

      const resp = await graphicAPI.getAll(Object.fromEntries(params))
      const data = resp.data;

      if (data.success) {
        // Handle both array and object with graphics property
        const graphicsData = Array.isArray(data.data) ? data.data : data.data.graphics || [];
        setGraphics(graphicsData);
      }
    } catch (error) {
      console.error('Error fetching graphics:', error);
      setGraphics([]); // Ensure graphics is always an array
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/v1/graphics/categories', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      const data = await response.json();

      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch mask assets
  const fetchMaskAssets = async () => {
    if (!isOpen) return;
    setMaskLoading(true);
    try {
      const params = {};
      if (maskSearch.trim()) params.search = maskSearch.trim();
      if (maskTypeFilter !== 'all') params.mediaType = maskTypeFilter;
      const response = await maskAssetAPI.list(params);
      if (response.data?.success) {
        setMaskAssets(response.data.data || []);
      } else {
        setMaskAssets([]);
      }
    } catch (error) {
      console.error('Error fetching mask assets:', error);
      setMaskAssets([]);
    } finally {
      setMaskLoading(false);
    }
  };

  const handleMaskAssetDelete = async (assetId) => {
    if (!window.confirm('Delete this mask asset?')) return;
    try {
      await maskAssetAPI.delete(assetId);
      fetchMaskAssets();
    } catch (error) {
      console.error('Error deleting mask asset:', error);
      alert(error?.response?.data?.detail || 'Failed to delete mask asset.');
    }
  };

  // Upload SVG file. Reads the file client-side and adds it to the local
  // graphics library so it previews immediately and can be placed on the
  // canvas — no backend round-trip (the server graphics endpoint is admin-only
  // and expects JSON, so multipart uploads there fail). Uploaded SVGs are kept
  // in localStorage so they persist across reloads.
  const handleSvgUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes('svg') && !file.name.toLowerCase().endsWith('.svg')) {
      alert('Please select an SVG file');
      event.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const text = await file.text();
      if (!/<svg[\s>]/i.test(text) || !/<\/svg>/i.test(text)) {
        alert('That file does not look like a valid SVG.');
        return;
      }

      const graphic = {
        id: `local-svg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: (file.name || 'SVG').replace(/\.svg$/i, ''),
        type: 'svg',
        // svgData powers both the grid preview and the placed item.
        svgData: text,
        isLocal: true,
      };

      setUploadedGraphics((prev) => {
        const next = [graphic, ...prev];
        try {
          localStorage.setItem('editor.uploadedGraphics', JSON.stringify(next));
        } catch (e) {
          // localStorage may be full or unavailable; preview still works in-session.
        }
        return next;
      });
    } catch (error) {
      console.error('Error reading SVG:', error);
      alert('Failed to read the SVG file. Please try again.');
    } finally {
      setUploading(false);
      // Reset the file input so selecting the same file again re-triggers change.
      event.target.value = '';
    }
  };

  // Remove a locally-uploaded SVG from the library.
  const removeUploadedGraphic = (id) => {
    setUploadedGraphics((prev) => {
      const next = prev.filter((g) => g.id !== id);
      try {
        localStorage.setItem('editor.uploadedGraphics', JSON.stringify(next));
      } catch (e) { /* ignore */ }
      return next;
    });
  };

  // Create category
  const createCategory = async () => {
    // Check admin access
    if (!isAdmin(user)) {
      showAdminAccessDialog(ADMIN_ACTIONS.CREATE_CATEGORY);
      return;
    }

    try {
      const response = await fetch('/api/v1/categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ ...newCategory, type: 'graphic' }),
      });

      if (response.ok) {
        setNewCategory({ name: '', description: '', color: '#6366F1' });
        fetchCategories();

        // Show success notification
        window.dispatchEvent(new CustomEvent('app:notify', {
          detail: {
            type: 'success',
            message: 'Graphics category created successfully!'
          }
        }));
      } else {
        // Handle HTTP error responses
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create category');
      }
    } catch (error) {
      console.error('Error creating category:', error);

      // Show error notification
      window.dispatchEvent(new CustomEvent('app:notify', {
        detail: {
          type: 'error',
          message: error.message || 'Failed to create category. Please try again.'
        }
      }));
    }
  };

  // Update category
  const updateCategory = async (id, data) => {
    try {
      const response = await fetch(`/api/v1/categories/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setEditingCategory(null);
        fetchCategories();
      }
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  // Delete category
  const deleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        const response = await fetch(`/api/v1/categories/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
          },
        });

        if (response.ok) {
          fetchCategories();
        }
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  // Use dialog search query when in dialog mode
  const effectiveSearchQuery = isDialog ? dialogSearchQuery : searchQuery;

  useEffect(() => {
    if (isOpen || isDialog) {
      setActiveTab(defaultTab || 'graphics');
    }
  }, [defaultTab, isOpen, isDialog]);

  useEffect(() => {
    if ((isOpen || isDialog) && activeTab === 'graphics') {
      fetchGraphics();
      fetchCategories();
    }
  }, [isOpen, isDialog, selectedCategory, effectiveSearchQuery, activeTab]);

  useEffect(() => {
    if (isOpen && activeTab === 'masking') {
      fetchMaskAssets();
    }
  }, [isOpen, activeTab, maskSearch, maskTypeFilter]);

  if (!isOpen && !isDialog) return null;

  return (
    <div className={isDialog 
      ? "h-full w-full bg-white flex flex-col" 
      : "fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-500 flex flex-col"
    }>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 
            className="text-lg font-semibold cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => onOpenDialog && onOpenDialog()}
            title="Click to open in large dialog"
          >
            Graphics
          </h2>
        </div>
        <div className="flex space-x-2">
          {/* Place an SVG directly on the canvas (inline svgContent item) */}
          {onImportSvgToCanvas && (
            <label className="p-2 hover:bg-gray-100 rounded cursor-pointer" title="Place SVG on canvas">
              <FileImage className="w-4 h-4" />
              <input
                type="file"
                accept=".svg,image/svg+xml"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onImportSvgToCanvas(file);
                  e.target.value = '';
                }}
                className="hidden"
              />
            </label>
          )}
          {/* Upload an SVG to the shared graphics library (server) */}
          <label className="p-2 hover:bg-gray-100 rounded cursor-pointer" title="Import SVG to library">
            <Upload className="w-4 h-4" />
            <input
              type="file"
              accept=".svg,image/svg+xml"
              onChange={handleSvgUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
          <button
            onClick={() => setShowCategoryManager(!showCategoryManager)}
            className="p-2 hover:bg-gray-100 rounded"
            title="Manage Categories"
          >
            <Edit className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tab selector */}
      <div className="flex border-b border-gray-200 text-sm font-medium">
        <button
          onClick={() => setActiveTab('graphics')}
          className={`flex-1 px-4 py-2 flex items-center justify-center gap-2 ${activeTab === 'graphics' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Layers className="w-4 h-4" />
          Graphics
        </button>
        <button
          onClick={() => setActiveTab('masking')}
          className={`flex-1 px-4 py-2 flex items-center justify-center gap-2 ${activeTab === 'masking' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <ImageIcon className="w-4 h-4" />
          Mask Frames
        </button>
      </div>

      {activeTab === 'graphics' && (
        <>
          {/* Search */}
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search graphics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto">

      {/* Category Manager */}
      {showCategoryManager && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h3 className="font-medium mb-3">Manage Categories</h3>

          {/* Add New Category */}
          <div className="space-y-2 mb-4">
            <input
              type="text"
              placeholder="Category name"
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={newCategory.description}
              onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
            <div className="flex space-x-2">
              <input
                type="color"
                value={newCategory.color}
                onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                className="w-10 h-8 border border-gray-300 rounded"
              />
              <button
                onClick={createCategory}
                disabled={!newCategory.name}
                className="flex-1 bg-indigo-600 text-white px-3 py-2 rounded text-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                Add
              </button>
            </div>
          </div>

          {/* Existing Categories */}
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {categories.map((category) => (
              <div key={category.id} className="flex items-center justify-between p-2 bg-white rounded border">
                {editingCategory === category.id ? (
                  <div className="flex-1 space-y-1">
                    <input
                      type="text"
                      defaultValue={category.name}
                      onBlur={(e) => updateCategory(category.id, { name: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 flex-1">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: category.color }}
                    />
                    <span className="text-sm">{category.name}</span>
                  </div>
                )}
                <div className="flex space-x-1">
                  <button
                    onClick={() => setEditingCategory(editingCategory === category.id ? null : category.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => deleteCategory(category.id)}
                    className="p-1 hover:bg-gray-100 rounded text-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category Chips */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1 rounded-full text-sm ${
              selectedCategory === 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.slug)}
              className={`px-3 py-1 rounded-full text-sm flex items-center space-x-1 ${
                selectedCategory === category.slug
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={{
                backgroundColor: selectedCategory === category.slug ? category.color : undefined,
              }}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: selectedCategory === category.slug ? 'white' : category.color }}
              />
              <span>{category.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="p-4 border-b border-gray-200 bg-blue-50">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-sm text-blue-600">Uploading SVG...</span>
          </div>
        </div>
      )}

        {/* Graphics Grid */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {[
                // Locally-uploaded SVGs first (filtered by the search box), then
                // the server library graphics.
                ...uploadedGraphics.filter((g) =>
                  !effectiveSearchQuery ||
                  (g.name || '').toLowerCase().includes(effectiveSearchQuery.toLowerCase())
                ),
                ...graphics,
              ].map((graphic) => (
                <div
                  key={graphic.id}
                  onClick={() => onGraphicSelect(graphic)}
                  className="cursor-pointer group relative"
                >
                  {graphic.isLocal && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeUploadedGraphic(graphic.id); }}
                      className="absolute top-1 right-1 z-10 p-1 rounded bg-white/90 border border-gray-200 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove uploaded SVG"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-2 group-hover:shadow-md transition-shadow flex items-center justify-center p-2">
                    {graphic.svgData ? (
                      <div
                        className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:max-w-full [&>svg]:max-h-full"
                        dangerouslySetInnerHTML={{ __html: graphic.svgData }}
                      />
                    ) : graphic.thumbnailUrl ? (
                      <img
                        src={graphic.thumbnailUrl}
                        alt={graphic.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <FileImage className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 truncate">{graphic.name}</h3>
                  <p className="text-xs text-gray-500">{graphic.isLocal ? 'Uploaded' : graphic.type}</p>
                  {graphic.isPremium && (
                    <span className="text-xs text-yellow-600">Premium</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {!loading && graphics.length === 0 && uploadedGraphics.length === 0 && (
            <div className="text-center text-gray-500 mt-8">
              <FileImage className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No graphics found</p>
              {searchQuery && (
                <p className="text-sm mt-1">Try adjusting your search or category filter</p>
              )}
              <p className="text-sm mt-2">Click the upload button to import SVG files</p>
            </div>
          )}
        </div>
          </div>
        </>
      )}

      <MaskAssetUploadDialog
        isOpen={showMaskDialog}
        onClose={() => setShowMaskDialog(false)}
        onUploaded={() => {
          fetchMaskAssets()
          setShowMaskDialog(false)
        }}
      />

      {activeTab === 'masking' && (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-gray-200 space-y-3">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search mask frames..."
                  value={maskSearch}
                  onChange={(e) => setMaskSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <select
                value={maskTypeFilter}
                onChange={(e) => setMaskTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
              </select>
            </div>
            <button
              onClick={() => setShowMaskDialog(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Add Mask Frame / Video
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {maskLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : maskAssets.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {maskAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="group border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white"
                  >
                    <button
                      onClick={() => onMaskSelect(asset)}
                      className="w-full text-left"
                    >
                      <div className="aspect-square bg-gray-100 relative overflow-hidden">
                        {asset.mediaType === 'video' ? (
                          asset.thumbnailUrl ? (
                            <img
                              src={asset.thumbnailUrl}
                              alt={asset.originalName || 'Mask video'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                              <Film className="w-8 h-8 opacity-80" />
                            </div>
                          )
                        ) : (
                          <img
                            src={asset.thumbnailUrl || asset.storageUrl}
                            alt={asset.originalName || 'Mask frame'}
                            className="w-full h-full object-contain bg-white"
                          />
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-sm font-medium text-gray-900 truncate">{asset.originalName || asset.filename}</p>
                        <p className="text-xs text-gray-500">
                          {asset.mediaType === 'video' ? 'Video Mask' : 'Image Mask'}
                          {asset.width && asset.height && ` • ${asset.width}x${asset.height}`}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center justify-between px-2 pb-2">
                      <span className="text-xs text-gray-400">{new Date(asset.createdAt || asset.updatedAt || Date.now()).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short' })}</span>
                      <button
                        onClick={() => handleMaskAssetDelete(asset.id)}
                        className="p-1 text-red-500 hover:text-red-600"
                        title="Delete mask asset"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 mt-8">
                <FileImage className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No mask frames found</p>
                <p className="text-sm mt-1">Upload PNG/SVG frames or videos with white reveal areas.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GraphicsSidebar;
